<script lang="ts">
  import { getPositionOnce, type Coords } from "../lib/geo";
  import { createNotice, uploadPhoto } from "../lib/wegli";
  import { DEFAULT_TBNR, TBNR_PRESETS } from "../lib/presets";

  type GpsState =
    | { kind: "idle" }
    | { kind: "locking" }
    | { kind: "locked"; coords: Coords; timestamp: number }
    | { kind: "error"; message: string };

  type SaveState =
    | { kind: "idle" }
    | { kind: "uploading" }
    | { kind: "creating" }
    | { kind: "saved"; token: string }
    | { kind: "error"; message: string };

  let gps: GpsState = { kind: "idle" };
  let save: SaveState = { kind: "idle" };
  let photo: File | null = null;
  let photoUrl = "";
  let registration = "";
  let tbnr = DEFAULT_TBNR;
  let showNote = false;
  let note = "";

  async function lockGps() {
    gps = { kind: "locking" };
    try {
      const coords = await getPositionOnce();
      gps = { kind: "locked", coords, timestamp: Date.now() };
    } catch (e) {
      gps = { kind: "error", message: e instanceof Error ? e.message : "GPS-Fehler" };
    }
  }

  function onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    photo = file;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    photoUrl = URL.createObjectURL(file);
  }

  function resetForm() {
    photo = null;
    if (photoUrl) {
      URL.revokeObjectURL(photoUrl);
      photoUrl = "";
    }
    registration = "";
    tbnr = DEFAULT_TBNR;
    showNote = false;
    note = "";
    save = { kind: "idle" };
    lockGps();
  }

  async function submit() {
    if (gps.kind !== "locked") {
      save = { kind: "error", message: "Kein GPS." };
      return;
    }
    if (!registration.trim()) {
      save = { kind: "error", message: "Kennzeichen fehlt." };
      return;
    }

    const coords = gps.coords;
    const startDate = new Date(gps.timestamp).toISOString();

    try {
      let signedId: string | undefined;
      if (photo) {
        save = { kind: "uploading" };
        signedId = await uploadPhoto(photo);
      }
      save = { kind: "creating" };
      const notice = await createNotice({
        registration: registration.trim().toUpperCase().replace(/\s+/g, ""),
        latitude: coords.latitude,
        longitude: coords.longitude,
        start_date: startDate,
        tbnr,
        note: note.trim() || undefined,
        photo_signed_id: signedId,
      });
      save = { kind: "saved", token: notice.token };
    } catch (e) {
      save = { kind: "error", message: e instanceof Error ? e.message : "Fehler" };
    }
  }

  lockGps();

  $: canSave =
    save.kind === "idle" &&
    gps.kind === "locked" &&
    registration.trim().length >= 2;
</script>

<div class="wrap">
  <section class="gps" class:ok={gps.kind === "locked"} class:warn={gps.kind === "locking"} class:err={gps.kind === "error"}>
    {#if gps.kind === "locking"}
      GPS wird ermittelt…
    {:else if gps.kind === "locked"}
      ✓ {gps.coords.latitude.toFixed(5)}, {gps.coords.longitude.toFixed(5)} (±{Math.round(gps.coords.accuracy)} m)
    {:else if gps.kind === "error"}
      ✗ GPS: {gps.message}
      <button type="button" on:click={lockGps}>Erneut versuchen</button>
    {:else}
      Warte auf GPS…
    {/if}
  </section>

  <label class="photo">
    {#if photoUrl}
      <img src={photoUrl} alt="Aufnahme" />
      <span>Foto ersetzen</span>
    {:else}
      <span class="photo-empty">📷 Foto aufnehmen</span>
    {/if}
    <input type="file" accept="image/*" capture="environment" on:change={onPhotoSelected} />
  </label>

  <label>
    <span class="lbl">Kennzeichen</span>
    <input
      type="text"
      bind:value={registration}
      autocapitalize="characters"
      autocomplete="off"
      spellcheck="false"
      placeholder="B-AB 1234"
      inputmode="text"
    />
  </label>

  <div class="presets">
    {#each TBNR_PRESETS as preset}
      <button
        type="button"
        class:active={tbnr === preset.tbnr}
        title={preset.hint}
        on:click={() => (tbnr = preset.tbnr)}
      >
        <strong>{preset.label}</strong>
      </button>
    {/each}
  </div>

  {#if showNote}
    <label>
      <span class="lbl">Notiz (optional)</span>
      <textarea bind:value={note} rows="2" placeholder="Kurzer Zusatz…"></textarea>
    </label>
  {:else}
    <button type="button" class="note-toggle" on:click={() => (showNote = true)}>+ Notiz hinzufügen</button>
  {/if}

  <button type="button" class="save" on:click={submit} disabled={!canSave}>
    {#if save.kind === "uploading"}Foto wird hochgeladen…
    {:else if save.kind === "creating"}Anzeige wird erstellt…
    {:else}Speichern &amp; nächster
    {/if}
  </button>

  {#if save.kind === "saved"}
    <div class="toast ok">
      ✓ Entwurf gespeichert (Token {save.token.slice(0, 8)}…).
      <button type="button" on:click={resetForm}>Weiter</button>
    </div>
  {:else if save.kind === "error"}
    <div class="toast err">✗ {save.message}</div>
  {/if}
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 12px; padding: 16px; max-width: 640px; margin: 0 auto; }

  .gps { padding: 10px 14px; border-radius: 10px; background: var(--surface); border: 1px solid var(--border); font-size: 0.9rem; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .gps.ok { border-color: var(--accent); color: var(--accent); }
  .gps.warn { border-color: var(--warn); color: var(--warn); }
  .gps.err { border-color: var(--err); color: var(--err); }

  .photo { position: relative; display: block; border-radius: 12px; overflow: hidden; background: var(--surface); border: 1px solid var(--border); min-height: 200px; cursor: pointer; }
  .photo input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
  .photo img { display: block; width: 100%; max-height: 50vh; object-fit: cover; }
  .photo span { display: block; text-align: center; padding: 12px; color: var(--muted); }
  .photo-empty { font-size: 1.1rem; padding: 48px 12px; }

  .lbl { display: block; color: var(--muted); font-size: 0.85rem; margin-bottom: 6px; }

  .presets { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 8px; }
  .presets button { text-align: left; display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; }
  .presets button strong { font-size: 0.95rem; }
  .presets button.active { border-color: var(--accent); background: color-mix(in oklab, var(--accent) 15%, var(--surface)); }

  .note-toggle { align-self: flex-start; color: var(--muted); background: transparent; border: none; padding: 4px 0; }

  .save { padding: 18px; font-size: 1.05rem; font-weight: 600; background: var(--accent); color: #0a0a0a; border-color: var(--accent); }
  .save[disabled] { background: var(--surface); color: var(--muted); border-color: var(--border); }

  .toast { padding: 12px 14px; border-radius: 10px; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
  .toast.ok { background: color-mix(in oklab, var(--accent) 20%, var(--surface)); border: 1px solid var(--accent); color: var(--accent); }
  .toast.err { background: color-mix(in oklab, var(--err) 20%, var(--surface)); border: 1px solid var(--err); color: var(--err); }
</style>
